import { financialYearFor, financialYearFromStartYear, istCalendarParts } from "@steelbill/tax";

/**
 * The one-tap date ranges a register's filter sheet offers (§11.1).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE COPY, BECAUSE "THIS YEAR" HAS TO MEAN THE SAME THING ON BOTH REGISTERS.
 * The bill, quotation and delivery-challan registers all offer these buttons
 * (`ChallanFilterSheet.tsx` has used them since §17.7 shipped). Two
 * copies of April–March arithmetic is two chances for one register to quietly
 * answer a different question from the other while both buttons read the same
 * word — and the owner compares the two totals.
 *
 * THE PRESETS COST NOTHING. Each is just a `from`/`to` pair computed here, sent
 * as the same two parameters a hand-typed range sends, so no endpoint knows
 * they exist. They are how the owner actually thinks about a date range: typing
 * two dates to answer "what did I bill this month?" is a chore nobody performs
 * twice.
 *
 * EVERY RANGE IS BUILT AS A STRING, from calendar parts read in Asia/Kolkata.
 * Never `date.toISOString().slice(0, 10)` and never `Date#getMonth()`: the son
 * reconciling on a laptop still set to another zone would get a "This month"
 * that starts on the 31st of the previous one, silently dropping a day's
 * documents off the top of the range with nothing on screen to say so.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export interface DatePreset {
  id: string;
  /** An i18n key. English never gets stored here — §11.4. */
  labelKey: string;
  range: () => { from?: string; to?: string };
}

/** `2026-04-01` — a calendar day, assembled as a string. */
function day(year: number, month: number, date: number): string {
  return `${String(year)}-${String(month).padStart(2, "0")}-${String(date).padStart(2, "0")}`;
}

/** The last day of a month, without constructing a Date in the local zone. */
function lastDayOf(year: number, month: number): number {
  // `Date.UTC(y, m, 0)` is the last day of month `m` (1-based) in UTC, which is
  // safe because only the day-of-month is read back out.
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export const DATE_PRESETS: DatePreset[] = [
  { id: "any", labelKey: "list.anyDate", range: () => ({ from: undefined, to: undefined }) },
  {
    id: "thisMonth",
    labelKey: "list.thisMonth",
    range: () => {
      const { year, month } = istCalendarParts(new Date());
      return { from: day(year, month, 1), to: day(year, month, lastDayOf(year, month)) };
    },
  },
  {
    id: "lastMonth",
    labelKey: "list.lastMonth",
    range: () => {
      const now = istCalendarParts(new Date());
      const year = now.month === 1 ? now.year - 1 : now.year;
      const month = now.month === 1 ? 12 : now.month - 1;
      return { from: day(year, month, 1), to: day(year, month, lastDayOf(year, month)) };
    },
  },
  {
    id: "thisFy",
    labelKey: "list.thisFy",
    range: () => {
      // April–March, the year the CA works in and the year invoice numbers
      // reset in (§6) — not a rolling twelve months.
      const fy = financialYearFor(new Date());
      return { from: day(fy.startYear, 4, 1), to: day(fy.endYear, 3, 31) };
    },
  },
  {
    id: "lastFy",
    labelKey: "list.lastFy",
    range: () => {
      const fy = financialYearFromStartYear(financialYearFor(new Date()).startYear - 1);
      return { from: day(fy.startYear, 4, 1), to: day(fy.endYear, 3, 31) };
    },
  },
];

/**
 * Which preset — if any — exactly describes the range currently in the URL, so
 * the button the owner pressed stays lit after the reload that follows it.
 */
export function activeDatePreset(
  from: string | undefined,
  to: string | undefined,
): DatePreset | undefined {
  return DATE_PRESETS.find((preset) => {
    const range = preset.range();
    return range.from === from && range.to === to;
  });
}
